import { useState } from "react";
import { UserPlus } from "lucide-react";
import api, { errorText } from "../../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { LinkBox } from "./LinkBox";

export const ROLE_LABEL = { customer: "Customer", partner: "Partner", staff: "Staff", admin: "Admin", owner: "Owner" };
const INVITABLE = { admin: ["partner", "staff", "customer"], owner: ["partner", "staff", "admin", "customer"] };

const Field = ({ label, children }) => (
  <label className="block space-y-1 text-[12px] font-semibold">
    <span>{label}</span>
    {children}
  </label>
);

const input = "h-9 w-full rounded-lg border border-input bg-background px-2 text-sm font-normal";

// Invite: makes the account (status "invited") and shows a 7-day single-use link to copy. Nothing is emailed.
export const InviteDialog = ({ actorRole, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "partner" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const close = (o) => { setOpen(o); if (!o) { setResult(null); setError(""); setForm({ email: "", name: "", role: "partner" }); } };
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/accounts/invite", { ...form, public_base: window.location.origin });
      setResult(data);
      onDone(data.account);
    } catch (err) {
      setError(errorText(err, "Could not create the invite."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" data-testid="accounts-invite-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-full bg-foreground px-3 py-1.5 text-[12px] font-semibold text-background transition-opacity hover:opacity-85">
        <UserPlus className="h-3.5 w-3.5" /> Invite
      </button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent data-testid="invite-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite someone</DialogTitle>
            <DialogDescription>They choose their password with the link. It works once and expires in 7 days.</DialogDescription>
          </DialogHeader>
          {result ? <LinkBox result={result} testid="invite-link" /> : (
            <form onSubmit={send} className="space-y-3">
              <Field label="Email"><input data-testid="invite-email" type="email" required value={form.email} className={input}
                onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Name (optional)"><input data-testid="invite-name" value={form.name} className={input}
                onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Role">
                <select data-testid="invite-role" value={form.role} className={input} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {(INVITABLE[actorRole] || []).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </Field>
              {error && <p data-testid="invite-error" className="text-[12px] text-rose-600">{error}</p>}
              <Button type="submit" data-testid="invite-submit" disabled={busy} className="w-full rounded-full">Create invite link</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
